import Phaser from 'phaser';
import { GAME } from '../constants';

export function createAmbientMotes(
  scene: Phaser.Scene,
  count = 10,
  depth = -114,
): Phaser.GameObjects.Arc[] {
  const motes: Phaser.GameObjects.Arc[] = [];
  for (let i = 0; i < count; i++) {
    const x = 30 + Math.random() * (GAME.WIDTH - 60);
    const y = 30 + Math.random() * (GAME.HEIGHT - 60);
    const r = 1 + Math.random() * 2;
    const alpha = 0.03 + Math.random() * 0.09;
    const color = i % 4 === 0 ? 0xffd27c : 0x6688bb;
    const mote = scene.add.circle(x, y, r, color, alpha).setDepth(depth);
    motes.push(mote);

    const driftUp = -(80 + Math.random() * 120);
    const sway = (Math.random() - 0.5) * 60;
    const dur = 6000 + Math.random() * 8000;

    scene.tweens.add({
      targets: mote,
      y: y + driftUp,
      x: x + sway,
      alpha: 0,
      duration: dur,
      ease: 'Sine.easeIn',
      delay: Math.random() * dur,
      onComplete: () => resetMote(scene, mote, depth),
    });
  }
  return motes;
}

function resetMote(
  scene: Phaser.Scene,
  mote: Phaser.GameObjects.Arc,
  depth: number,
): void {
  const x = 30 + Math.random() * (GAME.WIDTH - 60);
  const y = GAME.HEIGHT - 20 + Math.random() * 40;
  const alpha = 0.03 + Math.random() * 0.09;
  mote.setPosition(x, y);
  mote.setAlpha(alpha);
  mote.setDepth(depth);

  const driftUp = -(80 + Math.random() * 120);
  const sway = (Math.random() - 0.5) * 60;
  const dur = 6000 + Math.random() * 8000;

  scene.tweens.add({
    targets: mote,
    y: y + driftUp,
    x: x + sway,
    alpha: 0,
    duration: dur,
    ease: 'Sine.easeIn',
    onComplete: () => resetMote(scene, mote, depth),
  });
}

// --- Combat particle textures ---

function ensureTexture(scene: Phaser.Scene, key: string, color: number, size: number): void {
  if (scene.textures.exists(key)) return;
  const g = scene.add.graphics();
  g.fillStyle(color);
  g.fillRect(0, 0, size, size);
  g.generateTexture(key, size, size);
  g.destroy();
}

export function emitDamageParticles(scene: Phaser.Scene, x: number, y: number, amount: number): void {
  ensureTexture(scene, 'p_red', 0xff4444, 3);
  const emitter = scene.add.particles(x, y, 'p_red', {
    speed: { min: 40, max: 120 },
    angle: { min: 200, max: 340 },
    lifespan: 400,
    quantity: Math.min(amount * 2, 20),
    scale: { start: 1, end: 0 },
    alpha: { start: 0.9, end: 0 },
    emitting: false,
  });
  emitter.setDepth(200);
  emitter.explode();
  scene.time.delayedCall(500, () => emitter.destroy());
}

export function emitHealParticles(scene: Phaser.Scene, x: number, y: number): void {
  ensureTexture(scene, 'p_green', 0x66dd88, 3);
  const emitter = scene.add.particles(x, y, 'p_green', {
    speed: { min: 20, max: 60 },
    angle: { min: 250, max: 290 },
    lifespan: 600,
    quantity: 8,
    scale: { start: 1, end: 0 },
    alpha: { start: 0.8, end: 0 },
    emitting: false,
  });
  emitter.setDepth(200);
  emitter.explode();
  scene.time.delayedCall(700, () => emitter.destroy());
}

export function emitBlockParticles(scene: Phaser.Scene, x: number, y: number): void {
  ensureTexture(scene, 'p_blue', 0x4488ff, 3);
  const emitter = scene.add.particles(x, y, 'p_blue', {
    speed: { min: 30, max: 80 },
    angle: { min: 0, max: 360 },
    lifespan: 350,
    quantity: 10,
    scale: { start: 1, end: 0 },
    alpha: { start: 0.8, end: 0 },
    emitting: false,
  });
  emitter.setDepth(200);
  emitter.explode();
  scene.time.delayedCall(450, () => emitter.destroy());
}

export function emitComboParticles(scene: Phaser.Scene, x: number, y: number): void {
  ensureTexture(scene, 'p_gold', 0xffd700, 3);
  const emitter = scene.add.particles(x, y, 'p_gold', {
    speed: { min: 50, max: 150 },
    angle: { min: 0, max: 360 },
    lifespan: 500,
    quantity: 15,
    scale: { start: 1.2, end: 0 },
    alpha: { start: 1, end: 0 },
    emitting: false,
  });
  emitter.setDepth(200);
  emitter.explode();
  scene.time.delayedCall(600, () => emitter.destroy());
}
